import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Products from './Products';
import Cart from './Cart';
import { STATE } from '../classes/Enum';
import InfoPage from './settings/InfoPage';
import PrivacyPolicyPage from './settings/PrivacyPolicy';
import {getProduct as findProduct, getTitle } from '../helper/main';
import {serializeItem, serializeWishItem} from '../helper/products';
import Overview from './Overview';
import { getInventory, getProducts } from '../api/inventory';
import { useCart, useAppState } from "./Context";
import BootScreen from './BootScreen';
import TermsAndConditionsPage from './settings/TermsConditions';
import ProfileComponent from './settings/Profile';
import { CouponPage } from './settings/CouponPage';
import { logItemSelect } from '../api/events';
import { getWishlist, verifyUser } from '../api/user';
import OrderHistoryComponent from './settings/Orders';
import Collection from './collection/Collection';
import { useAuth } from './AuthProvider';
import { isAdminUser } from '../helper/admin';
import { resolveCardProductId } from '../api/cardResolve';
import {
  addRecentlyViewed,
  getRecentlyViewedIds,
  orderProductsByRecent,
} from '../helper/recentlyViewed';
import HeaderComponent from './Header';
import FooterComponent from './Footer';
import SideMenu from './settings/SideMenu';
import AdminTools from './admin/AdminTools';
import Spinner from './Spinner';
import { useNavigate } from 'react-router-dom';
import ItemInfoComponent from './ItemInfo';

const productStates = [STATE.SEALED, STATE.PACK, STATE.SINGLE, STATE.PROMO, STATE.SLAB];

const map = {
  singles: STATE.SINGLE,
  promotions: STATE.PROMO,
  packs: STATE.PACK,
};

const Main = ({ stripePromise, setSelectedItem, selectedItem, isBooting, setIsBooting, isMobile}) => {
  const [products, setProducts] = useState([]);
  const {cart} = useCart();
  const { user, bootstrap, authReady } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [recentVersion, setRecentVersion] = useState(0);
  const [menu, setMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const { section, id, cardId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const didInitRef = useRef(false);
  const {state, setState} = useAppState();

  function toggleMenu() {
    setMenu((m) => !m);
  }

  async function fetchWishlist() {
    const fetchedItems = await getWishlist();
    const serializedItems = fetchedItems.map(x => serializeWishItem(x));
    setWishlist(serializedItems);
    fetchWishlistItems(serializedItems);
  }

  async function fetchFirstPage() {
    const { items: fetchedProducts } = await getInventory(STATE.OVERVIEW, 1);
    if (!fetchedProducts || fetchedProducts.length === 0) {
        return;
      }
      addProducts(fetchedProducts, 1);
      setIsBooting(false);
  }

  async function fetchWishlistItems(serializedItems) {
    if (!Array.isArray(serializedItems) || serializedItems.length === 0) return;
    // Extract cardIds from wishlist lines
    const ids = [...new Set(
      serializedItems.map((w) => w.cardId).filter(Boolean)
    )];

    if (ids.length === 0) return;

    const products = await getProducts(ids); // returns array of products
    addProducts(products, 1);
  }

  useEffect(() => {
    if (user) {
      fetchWishlist();
    }
  }, [user]);

  useEffect(() => {
    if (!selectedItem?.id) return;
    addRecentlyViewed(selectedItem.id);
    setRecentVersion((v) => v + 1);
  }, [selectedItem?.id]);

  useEffect(() => {
    if (menu || state === STATE.OVERVIEW) {
      fetchRecentItems();
    }
  }, [menu, state, recentVersion]);


  async function fetchCartItems() {
    if (!Array.isArray(cart) || cart.length === 0) return;

    // Extract ids from cart lines
    const ids = [...new Set(
      cart.map((line) => line.productId ?? line.id).filter(Boolean)
    )];

    if (ids.length === 0) return;

    const products = await getProducts(ids); // <-- returns array of products
    addProducts(products, 1);

  }

  function getProduct(id) {
    return findProduct(products, id);
  }

  
  function isWished(productId) {
    return wishlist.some(wished => wished.cardId === productId);
  }

  function getWishedProducts() {
    return products.filter(product =>
      wishlist.some(w => w.cardId === product.id)
    );
  }

  function getRecentProducts() {
    void recentVersion;
    return orderProductsByRecent(products, getRecentlyViewedIds());
  }

  async function fetchRecentItems() {
    const ids = getRecentlyViewedIds();
    if (!ids.length) return;

    const loaded = new Set(products.map((p) => p.id));
    const missing = ids.filter((id) => !loaded.has(id));
    if (!missing.length) return;

    const fetched = await getProducts(missing);
    addProducts(fetched, 1);
  }


  function addProducts(products, page) {
    // resp can be an array or a single object
    const arr = Array.isArray(products) ? products : (products ? [products] : []);
    const fetched = arr.map(product => serializeItem(product, page));

    setProducts(prev => {
      const seen = new Set(prev.map(x => String(x.id)));
      const merged = [...prev];

      for (const p of fetched) {
        const key = String(p.id);
        if (!seen.has(key)) {
          merged.push(p);
          seen.add(key);
        }
      }
      return merged;
    });
  }

    // when hitting the browser, apply once
  useEffect(() => {
    
    const uid = searchParams.get("uid");
    const token = searchParams.get("token");

    if (cardId) {
      /* Card deep link handled once auth is ready — see effect below */
    } else if (section) {

      const key = section.toLowerCase();

      setState(map[key] ?? STATE.OVERVIEW);
    } else if (id) {
      (async () => {
        try {
          setState(STATE.SINGLE);
          const fetchedProduct = await getProducts([id]);
          addProducts(fetchedProduct, 1);
          setSelectedItem(serializeItem(fetchedProduct[0]));
          logItemSelect(id, "");
        } catch (e) {
          console.error(e);
        }
    })()
  } else if (uid && token) {
    verifyUser(uid, token);
    setState(STATE.OVERVIEW);
    navigate("/");
  } else {
    setState(STATE.OVERVIEW);
  }}, []);

  useEffect(() => {
    if (!cardId || !authReady) return;

    if (isAdminUser(user)) {
      setState(STATE.ADMIN_TOOLS);
      return;
    }

    let cancelled = false;

    (async () => {
      const productId = await resolveCardProductId(cardId);
      if (cancelled) return;

      if (!productId) {
        navigate("/", { replace: true });
        return;
      }

      try {
        setState(STATE.SINGLE);
        const fetchedProduct = await getProducts([productId]);
        if (cancelled) return;
        addProducts(fetchedProduct, 1);
        if (fetchedProduct?.[0]) {
          setSelectedItem(serializeItem(fetchedProduct[0]));
          logItemSelect(String(productId), "");
        }
        navigate(`/products/id/${productId}`, { replace: true });
      } catch (e) {
        console.error(e);
        if (!cancelled) navigate("/", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cardId, authReady, user, navigate]);

    useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const init = async () => {
      fetchFirstPage();
      fetchCartItems();

      const response = await bootstrap();

      if (response) {
        fetchWishlist();
      }
    };

    init();
  }, []);

  return (
  <div className='main'>
        {isBooting ? <BootScreen/> :
          <>
              {menu && (
                <SideMenu
                  toggleMenu={toggleMenu}
                  setState={setState}
                  recentProducts={getRecentProducts()}
                  onSelectRecent={(item) => {
                    setSelectedItem(item);
                    setState(STATE.OVERVIEW);
                  }}
                />
              )}
                {/* <HeaderComponent selectedItem={selectedItem} setSelectedItem={setSelectedItem} /> */}

              {((state !== STATE.CART)) && (
                <FooterComponent
                  state={state}
                  selectedItem={selectedItem}
                  setState={setState}
                  user={user}
                  toggleMenu={toggleMenu}
                  scrolled={isScrolled}
                  isMobile={isMobile}
                />
              )}
              
            {state === STATE.OVERVIEW && (
              <Overview
                products={products}
                setSelectedItem={setSelectedItem}
                state={state}
                recentProducts={getRecentProducts()}
              />
            )}
            {state === STATE.OVERVIEW && selectedItem && (
              <ItemInfoComponent
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                products={products}
                isWished={isWished}
                setWishlist={setWishlist}
              />
            )}
            {productStates.includes(state) && <Products products={products} setProducts={setProducts} state={state} setSelectedItem={setSelectedItem} selectedItem={selectedItem}
            isLoading={isLoading} setIsLoading={setIsLoading} addProducts={addProducts} hoveredId={hoveredId} setHoveredId={setHoveredId} isWished={isWished} setWishlist={setWishlist}
            isMobile={isMobile}  setIsScrolled={setIsScrolled}/>}
            {state === STATE.PROFILE && <ProfileComponent/>}
            {state === STATE.COUPONS && <CouponPage />}
            {state === STATE.CART && <Cart stripePromise={stripePromise} fetchProduct={getProduct} isMobile={isMobile}/>}
            {state === STATE.PRIVACY_POLICY && <PrivacyPolicyPage />}
            {state === STATE.TERMS_AND_CONDITIONS && <TermsAndConditionsPage />}
            {state === STATE.INFO && <InfoPage/>}
            {state === STATE.ORDERS && <OrderHistoryComponent products={products} />}
            {state === STATE.WISHLIST && <Products products={getWishedProducts()} setProducts={setProducts} state={state} selectedItem={selectedItem} setSelectedItem={setSelectedItem}
            isLoading={isLoading} setIsLoading={setIsLoading} addProducts={addProducts}
            hoveredId={hoveredId} setHoveredId={setHoveredId} isWished={isWished} setWishlist={setWishlist}
            isMobile={isMobile} setIsScrolled={setIsScrolled}/>}
            {state === STATE.COLLECTIONS && <Collection/>}
            {state === STATE.ADMIN_TOOLS && <AdminTools initialCardId={cardId} />}
            {isLoading && <Spinner />}
          </>
        }
  </div>
  );
};

export default Main;
